// usePlanForTier — fetch the active paystack_plans row for a tier_code.
//
// Used by PaywallCard to show price + currency + plan_code for the
// "Subscribe via Paystack" CTA. When no plan is seeded yet the hook
// returns plan=null and the card renders the "launching soon" copy.

import { useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";

export interface PaystackPlan {
  id: string;
  plan_code: string;
  tier_code: string;
  display_name: string;
  amount_kobo: number;
  currency: string;
  interval: string;
}

export interface UsePlanForTierState {
  plan: PaystackPlan | null;
  loading: boolean;
  error: string | null;
}

export function usePlanForTier(tierCode: string): UsePlanForTierState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const [plan, setPlan] = useState<PaystackPlan | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await (supabase.from as unknown as (t: string) => {
          select: (cols: string) => {
            eq: (a: string, b: unknown) => {
              eq: (a: string, b: unknown) => {
                order: (col: string, opts: { ascending: boolean }) => {
                  limit: (
                    n: number,
                  ) => Promise<{ data: PaystackPlan[] | null; error: { message: string } | null }>;
                };
              };
            };
          };
        })("paystack_plans")
          .select("id, plan_code, tier_code, display_name, amount_kobo, currency, interval")
          .eq("tier_code", tierCode)
          .eq("is_active", true)
          .order("amount_kobo", { ascending: true })
          .limit(1);

        if (cancelled) return;
        if (res.error) {
          setError(res.error.message);
          setPlan(null);
        } else {
          setPlan(res.data && res.data.length > 0 ? res.data[0]! : null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load plan");
        setPlan(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supabase, tierCode]);

  return { plan, loading, error };
}

export function formatPlanPrice(plan: PaystackPlan): string {
  const major = plan.amount_kobo / 100;
  try {
    return major.toLocaleString("en-GH", {
      style: "currency",
      currency: plan.currency,
      maximumFractionDigits: 0,
    });
  } catch {
    return `${plan.currency} ${major.toLocaleString()}`;
  }
}
